import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import './SystemProfile.css';

const SystemProfile = ({ profile, loading, onSave }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [avatarError, setAvatarError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name || '');
      setLastName(profile.last_name || '');
      setAvatarUrl(profile.avatar_url || '');
      setLinkedinUrl(profile.linkedin_url || '');
      setUserId(profile.user_id || null);
      setAvatarError(false); // Reset error when profile changes
    }
  }, [profile]);

  // Fetch user ID from auth if not in profile
  useEffect(() => {
    const fetchUserId = async () => {
      if (!userId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setUserId(user.id);
        }
      }
    };
    fetchUserId();
  }, [userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await onSave({
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        avatar_url: avatarUrl.trim() || null,
        linkedin_url: linkedinUrl.trim() || null,
      });
      setMessage('Profile updated successfully');
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const getMemberSince = () => {
    if (!profile?.created_at) return 'Unknown';
    const date = new Date(profile.created_at);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  if (loading && !profile) {
    return <div className="system-profile-loading">Loading profile...</div>;
    }

  return (
    <div className="system-profile">
      <div className="system-profile-header">
        <div className="profile-info">
          <div className="avatar-preview">
            {avatarUrl && !avatarError ? (
              <img 
                src={avatarUrl} 
                alt="Avatar preview"
                onError={() => setAvatarError(true)}
              />
            ) : (
              <div className="avatar-placeholder">
                {(firstName?.[0] || profile?.first_name?.[0] || profile?.last_name?.[0] || profile?.role?.[0] || '?').toUpperCase()}
              </div>
            )}
          </div>
          <div>
            <h2>{firstName || lastName ? `${firstName} ${lastName}`.trim() : 'Your Profile'}</h2>
            <p className="member-since">Member Since {getMemberSince()}</p>
            {profile?.role && <span className={`system-role-badge role-${profile.role}`}>{profile.role}</span>}
          </div>
        </div>
      </div>

      <form className="system-profile-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="firstName">First Name</label>
            <input
              id="firstName"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name"
            />
          </div>
          <div className="form-group">
            <label htmlFor="lastName">Last Name</label>
            <input
              id="lastName"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Last name"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="avatarUrl">Avatar URL</label>
          <input
            id="avatarUrl"
            type="url"
            value={avatarUrl}
            onChange={(e) => setAvatarUrl(e.target.value)}
            placeholder="https://example.com/avatar.jpg"
          />
          <small className="form-help">Add a link to your profile photo</small>
        </div>

        <div className="form-group">
          <label htmlFor="linkedinUrl">LinkedIn URL</label>
          <input
            id="linkedinUrl"
            type="url"
            value={linkedinUrl}
            onChange={(e) => setLinkedinUrl(e.target.value)}
            placeholder="https://www.linkedin.com/in/username"
          />
        </div>

        {error && <div className="form-error">{error}</div>}
        {message && <div className="form-success">{message}</div>}

        <div className="form-actions">
          <button type="submit" className="save-button" disabled={saving}>
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </form>

      <div className="password-section">
        <h3>Change Password</h3>
        <p className="password-section-subtitle">
          Set a new password for your FeatureMap account.
        </p>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setPasswordError(null);
            setPasswordMessage(null);

            if (!newPassword || newPassword.length < 6) {
              setPasswordError('Password must be at least 6 characters.');
              return;
            }

            if (newPassword !== confirmPassword) {
              setPasswordError('Passwords do not match.');
              return;
            }

            try {
              setPasswordLoading(true);
              await supabase.auth.updateUser({ password: newPassword });
              setPasswordMessage('Password updated successfully.');
              setNewPassword('');
              setConfirmPassword('');
              setTimeout(() => setPasswordMessage(null), 4000);
            } catch (err) {
              setPasswordError(err.message || 'Failed to update password.');
            } finally {
              setPasswordLoading(false);
            }
          }}
          className="password-form"
        >
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                minLength={6}
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmNewPassword">Confirm Password</label>
              <input
                id="confirmNewPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                minLength={6}
              />
            </div>
          </div>

          {passwordError && <div className="form-error">{passwordError}</div>}
          {passwordMessage && <div className="form-success">{passwordMessage}</div>}

          <div className="form-actions">
            <button type="submit" className="save-button" disabled={passwordLoading}>
              {passwordLoading ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </form>
        {userId && (
          <div className="user-id-display">
            <span className="user-id-label">User ID:</span>
            <span className="user-id-value">{userId}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemProfile;

